import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, CalendarCheck, RotateCcw, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const CATALOG = {
  balm: { name: "Manuka Oil Balm", price: "From $11.95", img: "/assets/dermagen-balm.png", url: "https://dermagen.net.au/products/manuka-oil-balm", ages: "Suitable from 6 months+", blurb: "Our original pharmacist-formulated balm with Manuka Oil and Propolis — the go-to for eczema flare-ups, dermatitis and minor wound healing." },
  lotion: { name: "Manuka Oil Lotion", price: "From $36.95", img: "/assets/dermagen-lotion.png", url: "https://dermagen.net.au/products/manuka-oil-lotion", ages: "3 years+", blurb: "Lightweight everyday hydration that calms dry, itchy skin and supports a healthy skin barrier." },
  serum: { name: "Active 8 Serum", price: "From $14.95", img: "/assets/dermagen-serum.png", url: "https://dermagen.net.au/products/active-8-serum-soothes-skin-inflammation", ages: "Adults", blurb: "A targeted serum with eight active botanicals to soothe inflammation, redness and stressed skin." },
  antiredness: { name: "Anti-Redness Cream", price: "$60.00", img: "/assets/dermagen-antiredness.png", url: "https://dermagen.net.au/products/anti-redness-cream", ages: "Adults", blurb: "Calms visible redness and evens skin tone for sensitive, reactive skin." },
  cream: { name: "Hydrating Manuka Oil Cream", price: "$29.95", img: "/assets/dermagen-cream.jpg", url: "https://dermagen.net.au/products/dermagen-hydrating-manuka-oil-cream", ages: "3 years+", blurb: "A rich daily cream crafted for dry, sensitive or irritated skin." },
  recovery: { name: "Skin Recovery Lotion", price: "From $36.95", img: "/assets/dermagen-recovery.png", url: "https://dermagen.net.au/products/skin-recovery-lotion", ages: "3 years+", blurb: "Designed to support sun-exposed and damaged skin barriers back to health." },
  dog: { name: "Doggy Skin Repair Balm", price: "From $11.95", img: "/assets/dermagen-dog.png", url: "https://dermagen.net.au/products/doggie-balm-infused-with-manuka-oil-lavender-oil", ages: "For pets", blurb: "Manuka Oil and Lavender balm for hot spots, dry paws and itchy skin on your best mate." },
};

const QUESTIONS = [
  {
    id: "concern",
    title: "What's your main skin concern?",
    options: [
      { value: "eczema", label: "Eczema, dermatitis or itchy flare-ups" },
      { value: "acne", label: "Pimples, acne or redness" },
      { value: "wound", label: "Minor wounds, cuts or scrapes" },
      { value: "dry", label: "Dry, sensitive everyday care" },
      { value: "sun", label: "Sun-exposed or damaged skin barrier" },
      { value: "pet", label: "My pet's skin or coat" },
    ],
  },
  {
    id: "who",
    title: "Who is it for?",
    options: [
      { value: "baby", label: "A baby (6 months+)" },
      { value: "child", label: "A child (3 years+)" },
      { value: "adult", label: "An adult" },
    ],
  },
  {
    id: "texture",
    title: "What texture do you prefer?",
    options: [
      { value: "balm", label: "A rich balm" },
      { value: "lotion", label: "A light lotion" },
      { value: "serum", label: "A targeted serum" },
    ],
  },
];

function getRecommendation(a) {
  if (a.concern === "pet") return { key: "dog" };
  if (a.concern === "wound") return { key: "balm", note: "For deeper, infected or non-healing wounds, please see our pharmacist or your GP." };
  if (a.concern === "eczema") {
    if (a.who === "baby") return { key: "balm" };
    if (a.who === "child") return { key: "lotion" };
    return { key: a.texture === "serum" ? "serum" : a.texture === "lotion" ? "lotion" : "balm" };
  }
  if (a.concern === "acne") {
    if (a.who && a.who !== "adult") return { key: "lotion", note: "For younger skin our pharmacist can tailor advice — book a free skin consult." };
    return { key: a.texture === "serum" ? "serum" : "antiredness" };
  }
  if (a.concern === "dry") return { key: a.texture === "balm" ? "cream" : a.texture === "serum" ? "serum" : "lotion" };
  return { key: "recovery" };
}

export default function QuizDialog({ open, onOpenChange }) {
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);

  const activeQuestions = QUESTIONS.filter(
    (q) => !(answers.concern === "pet" || answers.concern === "wound") || q.id === "concern"
  );

  const reset = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
  };

  const choose = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    const remaining = QUESTIONS.filter(
      (q) => !(next.concern === "pet" || next.concern === "wound") || q.id === "concern"
    );
    const idx = remaining.findIndex((q) => q.id === qid);
    if (idx === remaining.length - 1) {
      setResult(getRecommendation(next));
    } else {
      setStep(idx + 1);
    }
  };

  const bookConsult = () => {
    onOpenChange(false);
    setTimeout(() => document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" }), 150);
  };

  const product = result ? CATALOG[result.key] : null;
  const current = activeQuestions[step];

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent data-testid="quiz-dialog" className="max-w-lg overflow-hidden rounded-3xl border-border/60 p-0">
        <div className="bg-[#F4F7F4] px-7 pb-5 pt-7">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-greendark">DermaGen Skin Quiz</p>
          <h3 className="mt-2 font-serif text-2xl italic" data-testid="quiz-title">
            {result ? "Your perfect match" : "Find your perfect skincare match"}
          </h3>
          {!result && (
            <div className="mt-4 flex gap-1.5" data-testid="quiz-progress">
              {activeQuestions.map((q, i) => (
                <span key={q.id} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-brand-green" : "bg-border"}`} />
              ))}
            </div>
          )}
        </div>

        <div className="px-7 py-6">
          <AnimatePresence mode="wait">
            {!result && current ? (
              <motion.div
                key={current.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.25 }}
              >
                <p className="font-heading text-base font-bold" data-testid="quiz-question">{current.title}</p>
                <div className="mt-4 space-y-2.5">
                  {current.options.map((o) => (
                    <button
                      key={o.value}
                      data-testid={`quiz-option-${current.id}-${o.value}`}
                      onClick={() => choose(current.id, o.value)}
                      className="group flex w-full items-center justify-between rounded-xl border border-border/60 bg-white px-4 py-3 text-left text-sm font-medium transition-[border-color,background-color] duration-200 hover:border-brand-green hover:bg-brand-green/5"
                    >
                      {o.label}
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-brand-greendark" />
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : result && product ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="quiz-result"
              >
                <div className="flex items-center gap-5 rounded-2xl border border-brand-green/30 bg-[#F4F7F4] p-5">
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl bg-white p-2">
                    <img src={product.img} alt={product.name} className="max-h-full object-contain" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold" data-testid="quiz-result-name">{product.name}</p>
                    <p className="text-sm font-semibold text-brand-greendark">{product.price}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{product.ages}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{product.blurb}</p>
                {result.note && <p className="mt-2 rounded-lg bg-brand-blush px-3 py-2 text-xs font-medium text-brand-pinkdark" data-testid="quiz-result-note">{result.note}</p>}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild data-testid="quiz-shop-btn" className="rounded-full bg-foreground px-5 font-heading font-semibold text-white hover:bg-black">
                    <a href={product.url} target="_blank" rel="noopener noreferrer">Shop it <ExternalLink className="ml-1.5 h-4 w-4" /></a>
                  </Button>
                  <Button onClick={bookConsult} variant="outline" data-testid="quiz-consult-btn" className="rounded-full border-2 border-foreground/15 px-5 font-heading font-semibold hover:border-brand-pink hover:text-brand-pink">
                    <CalendarCheck className="mr-1.5 h-4 w-4" /> Book a skin consult
                  </Button>
                  <button onClick={reset} data-testid="quiz-retake-btn" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground">
                    <RotateCcw className="h-4 w-4" /> Retake
                  </button>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <p className="mt-6 border-t border-border/60 pt-4 text-xs text-muted-foreground">
            General guidance only — our pharmacist can confirm the best fit for your skin, in store or on (08) 9757 2224.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
